import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "services/ServiceUtil";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";

const SLICE = "GLOBAL_USER";
export let userList: any = [];

export function setUserList(newList: any[]) {
  userList = newList;
}

const initialState: any = {
  globalUserDetail: [],
  globalUserListLoading: true,
  totalGlobalUser: 0,
  category: "Property",
  globalPlanList: [],
  globalInncodeList: [],
  templatePlanList: [],
  propertyPlanList: [],
  planOwnerList: [],
  searchLoading: false,
  latestSearchRequestId: null
};

export const fetchGlobalUser = createAsyncThunk(`${SLICE}/fetchGlobalUser`, (data: any, thunkApi) => {
  const props = {
    innCodes: data?.innCode || "",
    page: data.page,
    size: data.size,
    searchText: data.value,
    includecorpusers: data?.includecorpusers ?? false
  };
  return apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], props);
});

export const fetchGlobalPlansAsync = createAsyncThunk(`${SLICE}/fetchGlobalPlansAsync`, (data: any, thunkApi) => {
  return apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data, thunkApi, [], { externalHltId: data?.externalHltId || "" });
});

export const fetchGlobalInncodes = createAsyncThunk(`${SLICE}/fetchGlobalInncodes`, async (data: any, thunkApi) => {
  const response = await apiRequest(LIBRARY_SERVICE_METHOD.getInncode, {}, thunkApi, [], {
    searchText: data.searchText
  });
  return {
    response,
    data
  };
});

export const fetchOwnerDetailsAsync = createAsyncThunk(`${SLICE}/fetchOwnerDetailsAsync`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], { size: data.size, hltIds: data.value })
);

export const fetchMultiTargeted = createAsyncThunk(`${SLICE}/fetchOwnerDetailsAsync`, (data: any, thunkApi) => {
  return apiRequest(LIBRARY_SERVICE_METHOD.getMultiTargated, data, thunkApi);
});

export const fetchInncodeInfo = createAsyncThunk(`${SLICE}/inncodeInfo`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.postInncodeInfo, data, thunkApi)
);

export const fetchTemplatePropertyPlansAsync = createAsyncThunk(
  `${SLICE}/fetchTemplatePropertyPlansAsync`,
  async (data: any, thunkApi) => {
    const params = {
      page: data.page,
      size: 100
    };

    const payload = {
      ...data.payload
    };

    const plansResponse = await apiRequest(
      USER_PLAN_SERVICE_METHOD.postAboveProperty,
      payload,
      thunkApi,
      ["plans"],
      params
    );

    const userPlans = plansResponse?.userplans ?? [];
    const planOwners = plansResponse?.meta?.owners ?? [];

    let ownerResponse = null;

    if (planOwners.length > 0 && data?.payload?.planType === "PLAN") {
      ownerResponse = await apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], {
        size: planOwners.length,
        hltIds: planOwners
      });
    }

    return {
      userPlans,
      ownerResponse,
      planType: data?.payload?.planType
    };
  }
);

export const GlobalSearchSlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.globalUserDetail = [];
    },
    clearPlanSearch: (state) => {
      state.globalPlanList = [];
    },
    clearInnCodeSearch: (state) => {
      state.globalInncodeList = [];
    },
    setCategory: (state, action) => {
      state.category = action.payload;
    },
    setTemplatePlanList: (state, action) => {
      state.templatePlanList = action.payload;
    },
    setPropertyPlanList: (state, action) => {
      state.propertyPlanList = action.payload;
    },
    setPlanOwner: (state, action) => {
      state.planOwnerList = action.payload;
    },
    setSearchLoadingCount: (state, action) => {
      state.searchLoading = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchGlobalUser.pending, (state, action) => {
        state.globalUserListLoading = true;
        state.searchLoading = true;
      })
      .addCase(fetchGlobalUser.fulfilled, (state, action) => {
        state.globalUserListLoading = false;
        state.totalGlobalUser = action.payload?.meta?.page?.total;

        const newUsers = action.payload?.users;
        const page = action?.meta?.arg?.page;
        if (page === 0) {
          userList = newUsers;
          state.globalUserDetail = userList;
        } else {
          userList = [...userList, ...newUsers];
          state.globalUserDetail = userList;
        }
        state.searchLoading = false;
      })
      .addCase(fetchGlobalUser.rejected, (state, action) => {
        state.globalUserListLoading = false;
        state.globalUserDetail = [];
        state.searchLoading = false;
      })
      .addCase(fetchGlobalPlansAsync.pending, (state, action: any) => {
        state.searchLoading = true;
      })
      .addCase(fetchGlobalPlansAsync.fulfilled, (state, action: any) => {
        state.globalPlanList = action?.payload?.plans;
        state.searchLoading = false;
      })
      .addCase(fetchGlobalPlansAsync.rejected, (state, action: any) => {
        state.searchLoading = false;
      })
      .addCase(fetchGlobalInncodes.pending, (state, action: any) => {
        state.searchLoading = true;
      })
      .addCase(fetchGlobalInncodes.fulfilled, (state, action: any) => {
        const isAboverPropertyAndHotel = action?.payload?.data?.isAbovePropertyUser;
        if (isAboverPropertyAndHotel) {
          const innode = action?.payload?.data?.inncode.split(",");
          const filteredHotels = action?.payload?.response?.properties.filter((hotel: any) =>
            innode.includes(hotel.innCode)
          );
          state.globalInncodeList = filteredHotels ?? [];
        } else {
          state.globalInncodeList = action?.payload?.response?.properties;
        }
        state.searchLoading = false;
      })
      .addCase(fetchGlobalInncodes.rejected, (state, action: any) => {
        state.searchLoading = false;
      })
      .addCase(fetchMultiTargeted.pending, (state, action: any) => {
        state.searchLoading = true;
      })
      .addCase(fetchMultiTargeted.fulfilled, (state, action: any) => {
        state.globalPlanList = action.payload.plans;
        state.searchLoading = false;
      })
      .addCase(fetchMultiTargeted.rejected, (state, action: any) => {
        state.searchLoading = true;
      })
      .addCase(fetchTemplatePropertyPlansAsync.pending, (state, action: any) => {
        state.searchLoading = true;
        state.latestSearchRequestId = action.meta.requestId;
      })
      .addCase(fetchTemplatePropertyPlansAsync.fulfilled, (state, action: any) => {
        const { userPlans, ownerResponse, planType } = action.payload;
        // Ignore old request's response
        if (state.latestSearchRequestId !== action.meta.requestId) {
          return;
        }
        if (planType === "PLAN") {
          state.propertyPlanList = userPlans;

          state.planOwnerList = ownerResponse?.users ?? [];
        } else if (planType === "TEMPLATE_PLAN") {
          state.templatePlanList = userPlans;
        }
        state.searchLoading = false;
        state.latestSearchRequestId = null;
      })
      .addCase(fetchTemplatePropertyPlansAsync.rejected, (state, action: any) => {
        if (state.latestSearchRequestId !== action.meta.requestId) {
          return;
        }
        state.searchLoading = false;
        state.latestSearchRequestId = null;
      })
      .addCase(fetchInncodeInfo.pending, (state, action: any) => {
        state.searchLoading = true;
      })
      .addCase(fetchInncodeInfo.fulfilled, (state, action: any) => {
        const innCodeDetails = action.payload.innCodeDetails;
        const modifiedData = innCodeDetails.map((data: any) => {
          return {
            innCode: data.innCode,
            name: data.propName,
            shortName: data.mgmtCompany
          };
        });
        state.globalInncodeList = modifiedData ?? [];
        state.searchLoading = false;
      })
      .addCase(fetchInncodeInfo.rejected, (state, action: any) => {
        state.searchLoading = false;
      });
  }
});

export const {
  clearSearch,
  setCategory,
  clearPlanSearch,
  clearInnCodeSearch,
  setTemplatePlanList,
  setPropertyPlanList,
  setPlanOwner,
  setSearchLoadingCount
} = GlobalSearchSlice.actions;

export default GlobalSearchSlice.reducer;
